import { useState, useEffect } from 'react';

import { maintenanceApi, MaintenanceConfig } from '@/lib/api/maintenance';

let maintenanceCache: MaintenanceConfig | null = null;
let fetchPromise: Promise<MaintenanceConfig> | null = null;
let lastFetchTime = 0;

const REFETCH_INTERVAL = 60_000; // Re-fetch every 60s (maintenance state can change)

const defaultConfig: MaintenanceConfig = {
  enabled: false,
};

/**
 * Hook to access maintenance mode status.
 * Unlike useFeatures, this re-fetches periodically since maintenance state changes.
 */
export function useMaintenanceMode() {
  const [config, setConfig] = useState<MaintenanceConfig>(
    maintenanceCache || defaultConfig,
  );
  const [loading, setLoading] = useState(!maintenanceCache);

  useEffect(() => {
    const fetchConfig = (): void => {
      const now = Date.now();

      // Use cache if recent enough
      if (maintenanceCache && now - lastFetchTime < REFETCH_INTERVAL) {
        setConfig(maintenanceCache);
        setLoading(false);
        return;
      }

      // If fetch already in progress, wait for it
      if (fetchPromise) {
        fetchPromise
          .then((data) => {
            setConfig(data);
            setLoading(false);
          })
          .catch(() => {
            setConfig(maintenanceCache || defaultConfig);
            setLoading(false);
          });
        return;
      }

      // Start new fetch
      fetchPromise = maintenanceApi.getMaintenanceConfig();
      fetchPromise
        .then((data) => {
          maintenanceCache = data;
          lastFetchTime = Date.now();
          setConfig(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch maintenance config:', error);
          setConfig(maintenanceCache || defaultConfig);
          setLoading(false);
        })
        .finally(() => {
          fetchPromise = null;
        });
    };

    fetchConfig();

    // Poll periodically
    const interval = setInterval(fetchConfig, REFETCH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const isActive = config.enabled;
  const isScheduled = !config.enabled && !!config.scheduledAt;

  return { config, loading, isActive, isScheduled };
}
